import { IconEye, IconEyeOff } from '@tabler/icons-react'
import { useState } from 'react'
import { type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { Input } from '~/components/ui/input'
import { ControlledFormItem } from './controlled-form-item'

type FormInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>
  name: TName
  label?: string
  required?: boolean
  placeholder?: string
  type?: string
  id?: string
  disabled?: boolean
}

export function FormInput<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  required,
  placeholder,
  type = 'text',
  id,
  disabled,
}: FormInputProps<TFieldValues, TName>) {
  const inputId = id || String(name)

  return (
    <ControlledFormItem
      control={control}
      name={name}
      label={label}
      required={required}
      render={(field) => <Input {...field} id={inputId} type={type} placeholder={placeholder} disabled={disabled} />}
    />
  )
}

type FormPasswordProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>
  name: TName
  label?: string
  required?: boolean
  placeholder?: string
  id?: string
  disabled?: boolean
}

export function FormPassword<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  required,
  placeholder,
  id,
  disabled,
}: FormPasswordProps<TFieldValues, TName>) {
  const [showPassword, setShowPassword] = useState(false)
  const inputId = id || String(name)

  return (
    <ControlledFormItem
      control={control}
      name={name}
      label={label}
      required={required}
      render={(field) => (
        <div className="relative">
          <Input
            {...field}
            id={inputId}
            type={showPassword ? 'text' : 'password'}
            placeholder={placeholder}
            disabled={disabled}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
          >
            {showPassword ? <IconEyeOff className="h-4 w-4" /> : <IconEye className="h-4 w-4" />}
          </button>
        </div>
      )}
    />
  )
}
